import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/Navbar";
import CreatorTopBar from "../../components/ArtHouse/CreatorTopBar";

import {
  fetchMyArtworks,
  fetchMyArtworkDetail,
  updateMyArtwork,
  deleteMyArtwork,
} from "../../services/arthouseApi";

import MerchLinksEditor, {
  merchBuild,
  DEFAULT_LINK,
} from "../../components/ArtHouse/MerchLinksEditor";

function parseTags(tags) {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags.filter(Boolean);
  if (typeof tags === "string") {
    return tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * Backend might return:
 * A) { artwork: {...}, tags: [...], merchLinks: [...] }
 * B) { artwork: {..., tags: [...], merchLinks: [...] } }
 * C) { ...artwork } only
 *
 * This accepts ANY of those and returns editor rows.
 */
function normalizeMerchLinks(raw) {
  const source =
    raw?.merchLinks ||
    raw?.merch_links ||
    raw?.merch ||
    raw?.merch_links_array ||
    raw?.artwork?.merchLinks ||
    raw?.artwork?.merch_links ||
    raw?.artwork?.merch ||
    raw?.artwork?.merch_links_array ||
    [];

  const arr = Array.isArray(source) ? source : [];
  if (!arr.length) return [DEFAULT_LINK()];

  return arr.map((x) => ({
    ...DEFAULT_LINK(),
    label: x?.label || "",
    platform: x?.platform || "",
    category: x?.category || "other",
    product_type: x?.type || x?.product_type || "",
    purchase_url: x?.purchase_url || x?.url || "",
    price: x?.price != null ? String(x.price) : "",
    currency: x?.currency || "USD",
    sizes: Array.isArray(x?.sizes) ? x.sizes : [],
    colors: Array.isArray(x?.colors) ? x.colors : [],
    note: x?.note || "",
    is_primary: !!x?.is_primary,
    expanded: false,
  }));
}

export default function EditArtworkPage() {
  const { artworkId } = useParams();
  const nav = useNavigate();

  const numericArtworkId = useMemo(() => {
    const n = Number(artworkId);
    return Number.isFinite(n) ? n : artworkId;
  }, [artworkId]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [errorMsg, setErrorMsg] = useState("");
  const [flash, setFlash] = useState("");

  const [original, setOriginal] = useState(null);

  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [tagsText, setTagsText] = useState("");

  const [links, setLinks] = useState([DEFAULT_LINK()]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErrorMsg("");
      setFlash("");

      try {
        let detail = null;

        try {
          detail = await fetchMyArtworkDetail(numericArtworkId);
        } catch {
          const myArt = await fetchMyArtworks();
          const list = myArt?.artworks || [];
          const found =
            list.find(
              (a) => String(a?.id ?? a?.artwork_id) === String(numericArtworkId)
            ) || null;

          detail = found ? { artwork: found } : null;
        }

        if (!alive) return;

        const artwork = detail?.artwork || detail || null;

        if (!artwork) {
          setErrorMsg("Artwork not found in your uploads.");
          setOriginal(null);
          setLinks([DEFAULT_LINK()]);
          setTagsText("");
          setTitle("");
          setCaption("");
          setLoading(false);
          return;
        }

        setOriginal(artwork);
        setTitle(artwork.title || "");
        setCaption(artwork.description || "");

        const tagsFromApi = detail?.tags ?? artwork?.tags ?? [];
        setTagsText(parseTags(tagsFromApi).join(", "));

        setLinks(normalizeMerchLinks(detail));

        setLoading(false);
      } catch (e) {
        if (!alive) return;
        setErrorMsg(e?.message || "Failed to load artwork.");
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [numericArtworkId]);

  const tagsArr = useMemo(() => parseTags(tagsText), [tagsText]);

  const { merchLinks, merchErrors } = useMemo(() => merchBuild(links), [links]);

  const canSave = useMemo(() => {
    return !saving && !loading && !!original && merchErrors.length === 0;
  }, [saving, loading, original, merchErrors.length]);

  const payloadForApi = useMemo(() => {
    return {
      title: title.trim(),
      description: caption.trim(),
      tags: tagsArr,
      tags_text: tagsArr.join(", "),
      merchLinks,
      merch_links: merchLinks,
      merch_links_array: merchLinks,
      merch: merchLinks,
    };
  }, [title, caption, tagsArr, merchLinks]);

  function onCancel() {
    nav("/dashboard/art");
  }

  async function onSave() {
    if (!original) return;

    setSaving(true);
    setErrorMsg("");
    setFlash("");

    try {
      // IMPORTANT: use returned payload to rehydrate UI
      const updatedResp = await updateMyArtwork(numericArtworkId, payloadForApi);

      const updatedArtwork = updatedResp?.artwork || updatedResp || null;
      const updatedTags = updatedResp?.tags ?? updatedArtwork?.tags ?? tagsArr;

      setOriginal(updatedArtwork || original);
      setTitle(updatedArtwork?.title || title);
      setCaption(updatedArtwork?.description || caption);
      setTagsText(parseTags(updatedTags).join(", "));
      setLinks(normalizeMerchLinks(updatedResp));

      setFlash("Saved ");
      setTimeout(() => setFlash(""), 900);

      nav("/dashboard/art", { replace: true });
    } catch (e) {
      const backendMsg =
        e?.data?.error ||
        e?.data?.message ||
        e?.message ||
        "Failed to save changes.";
      setErrorMsg(backendMsg);
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!original) return;

    setDeleting(true);
    setErrorMsg("");
    setFlash("");

    try {
      await deleteMyArtwork(numericArtworkId);
      setShowDeleteModal(false);
      nav("/dashboard/art", { replace: true });
    } catch (e) {
      const backendMsg =
        e?.data?.error ||
        e?.data?.message ||
        e?.message ||
        "Failed to delete artwork.";
      setErrorMsg(backendMsg);
    } finally {
      setDeleting(false);
    }
  }

  const imageUrl = useMemo(() => {
    return (original?.image_url || original?.imageUrl || "").trim();
  }, [original]);

  const createdLabel = useMemo(() => {
    const d = original?.created_at ? new Date(original.created_at) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : "";
  }, [original]);

  return (
    <div className="min-h-screen bg-[#070B16] text-white">
      <Navbar />

      {/* ambience */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 left-16 h-[560px] w-[560px] rounded-full bg-cyan-400/10 blur-[150px]" />
        <div className="absolute top-28 -right-52 h-[720px] w-[720px] rounded-full bg-sky-400/10 blur-[170px]" />
        <div className="absolute -bottom-60 left-1/2 h-[720px] w-[720px] -translate-x-1/2 rounded-full bg-indigo-400/5 blur-[170px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_10%,rgba(56,189,248,0.08),transparent_45%),radial-gradient(circle_at_80%_18%,rgba(125,211,252,0.06),transparent_50%),radial-gradient(circle_at_55%_80%,rgba(99,102,241,0.05),transparent_55%)]" />
        <div className="absolute inset-0 opacity-[0.18] [background-image:linear-gradient(to_right,rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:44px_44px]" />
      </div>

      <div className="relative mx-auto w-full max-w-[1200px] px-4 pt-8 pb-16">
        <CreatorTopBar />

        {/* top breadcrumbs */}
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-white/60">
            <Link to="/dashboard/art" className="hover:text-white/80 transition">
              Dashboard
            </Link>
            <span className="text-white/35">/</span>
            <span className="text-white/80 font-semibold">Edit</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              disabled={loading || !original || saving}
              className="
                h-10 px-4 rounded-full
                border border-red-400/25 bg-red-400/10
                hover:bg-red-400/15
                transition text-sm font-semibold text-red-200
                disabled:opacity-50
              "
            >
              Delete
            </button>

            <button
              type="button"
              onClick={onCancel}
              className="
                h-10 px-4 rounded-full
                border border-white/10 bg-white/[0.04]
                hover:bg-white/[0.07]
                transition text-sm font-semibold text-white/80
              "
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={onSave}
              disabled={!canSave}
              className="
                h-10 px-4 rounded-full
                border border-sky-300/25 bg-sky-300/10
                hover:bg-sky-300/15
                transition text-sm font-semibold text-sky-200
                disabled:opacity-50
              "
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {flash ? (
          <div className="mt-4 rounded-2xl border border-sky-300/25 bg-sky-300/10 px-4 py-3 text-sm text-sky-200 shadow-[0_18px_80px_rgba(0,0,0,0.35)]">
            {flash}
          </div>
        ) : null}

        {errorMsg ? (
          <div className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-white/80 shadow-[0_18px_80px_rgba(0,0,0,0.35)]">
            {errorMsg}
          </div>
        ) : null}

        {merchErrors.length ? (
          <div className="mt-4 rounded-2xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-sm text-red-200">
            Fix merch links:{" "}
            <span className="text-white/70">
              {merchErrors.slice(0, 2).map((e, idx) => (
                <span key={idx}>
                  Link {e.idx + 1}: {e.msg}
                  {idx === 1 ? "" : " • "}
                </span>
              ))}
              {merchErrors.length > 2 ? ` +${merchErrors.length - 2} more` : ""}
            </span>
          </div>
        ) : null}

        {loading ? (
          <div className="mt-6 rounded-[28px] border border-white/10 bg-white/[0.045] backdrop-blur-xl p-6 text-white/70 shadow-[0_26px_120px_rgba(0,0,0,0.55)]">
            Loading…
          </div>
        ) : !original ? null : (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left preview */}
            <div className="lg:col-span-5">
              <div className="rounded-[30px] border border-white/10 bg-white/[0.055] backdrop-blur-xl shadow-[0_30px_140px_rgba(0,0,0,0.6)] overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-[11px] tracking-[0.36em] uppercase text-white/55">
                      Post
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] tracking-[0.32em] uppercase text-white/45">
                        Created
                      </div>
                      <div className="mt-1 text-xs text-white/60">
                        {createdLabel || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 rounded-[22px] border border-white/10 bg-black/15 overflow-hidden">
                    <div className="relative aspect-[4/3] bg-black/30">
                      {imageUrl ? (
                        <>
                          <img
                            src={imageUrl}
                            alt="Artwork"
                            className="absolute inset-0 h-full w-full object-cover opacity-[0.96]"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-black/25" />
                        </>
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-sm text-white/45">
                          No image
                        </div>
                      )}
                    </div>

                    <div className="p-5">
                      <div className="text-lg font-semibold text-white/90">
                        {title.trim() || original?.title || "Untitled"}
                      </div>

                      <div className="mt-2 text-sm text-white/60 whitespace-pre-line">
                        {caption.trim() ||
                          original?.description ||
                          "No caption."}
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {tagsArr.slice(0, 12).map((t) => (
                          <span
                            key={t}
                            className="text-xs rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-white/70"
                          >
                            #{t}
                          </span>
                        ))}
                        {tagsArr.length === 0 ? (
                          <span className="text-xs text-white/45">No tags</span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <div className="text-sm font-semibold text-white/85">
                      Shop Links Preview
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {merchLinks.slice(0, 4).map((m, idx) => (
                        <span
                          key={idx}
                          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${
                            m.is_primary
                              ? "border-sky-300/25 bg-sky-300/10 text-sky-200"
                              : "border-white/10 bg-white/[0.04] text-white/70"
                          }`}
                        >
                          {m.label || m.platform || m.type || m.product_type}
                        </span>
                      ))}

                      {merchLinks.length === 0 ? (
                        <span className="text-xs text-white/45">
                          No shop links
                        </span>
                      ) : null}

                      {merchLinks.length > 4 ? (
                        <span className="text-xs text-white/45">
                          +{merchLinks.length - 4} more
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right form */}
            <div className="lg:col-span-7">
              <div className="rounded-[30px] border border-white/10 bg-white/[0.055] backdrop-blur-xl shadow-[0_30px_140px_rgba(0,0,0,0.6)] overflow-hidden">
                <div className="px-6 pt-6 pb-4 border-b border-white/10">
                  <div className="text-[11px] tracking-[0.36em] uppercase text-white/55">
                    Edit details
                  </div>
                  <div className="mt-1 text-sm text-white/65">
                    Update and save. Media stays the same.
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid gap-4">
                    <div>
                      <label className="text-sm font-semibold text-white/70">
                        Title
                      </label>
                      <input
                        className="mt-2 w-full rounded-2xl border border-white/12 bg-black/25 px-4 py-3 text-slate-200 outline-none focus:ring-2 focus:ring-sky-400/30 focus:border-sky-300/25"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Short title"
                        maxLength={80}
                      />
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-white/70">
                        Caption
                      </label>
                      <textarea
                        className="mt-2 w-full min-h-[160px] rounded-2xl border border-white/12 bg-black/25 px-4 py-3 text-slate-200 outline-none focus:ring-2 focus:ring-sky-400/30 focus:border-sky-300/25"
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        placeholder="Short, clean caption…"
                        maxLength={2200}
                      />
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-white/70">
                        Tags (comma separated)
                      </label>
                      <input
                        className="mt-2 w-full rounded-2xl border border-white/12 bg-black/25 px-4 py-3 text-slate-200 outline-none focus:ring-2 focus:ring-sky-400/30 focus:border-sky-300/25"
                        value={tagsText}
                        onChange={(e) => setTagsText(e.target.value)}
                        placeholder="anime, portrait, character"
                      />
                    </div>

                    <MerchLinksEditor
                      links={links}
                      setLinks={setLinks}
                      merchErrors={merchErrors}
                    />
                  </div>

                  {/* Delete modal */}
                  {showDeleteModal && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
                      <div
                        className="absolute inset-0 bg-black/60"
                        onClick={() => (!deleting ? setShowDeleteModal(false) : null)}
                      />
                      <div className="relative w-full max-w-[520px] rounded-[26px] border border-white/12 bg-[#0A1022]/90 backdrop-blur-xl shadow-[0_30px_140px_rgba(0,0,0,0.7)] overflow-hidden">
                        <div className="px-6 py-5 border-b border-white/10">
                          <div className="text-[11px] tracking-[0.35em] uppercase text-white/50">
                            Confirm Delete
                          </div>
                          <div className="mt-2 text-lg font-semibold text-white/90">
                            Delete this post permanently?
                          </div>
                          <div className="mt-2 text-sm text-white/60">
                            This can’t be undone.
                          </div>
                        </div>
                        <div className="px-6 py-5 flex items-center justify-end gap-2">
                          <button
                            type="button"
                            disabled={deleting}
                            onClick={() => setShowDeleteModal(false)}
                            className="h-11 px-5 rounded-2xl border border-white/12 bg-white/[0.04] hover:bg-white/[0.08] transition text-sm font-semibold text-white/80 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            disabled={deleting}
                            onClick={confirmDelete}
                            className="h-11 px-5 rounded-2xl border border-red-400/25 bg-red-400/10 hover:bg-red-400/15 transition text-sm font-semibold text-red-200 disabled:opacity-50"
                          >
                            {deleting ? "Deleting…" : "Yes, Delete"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
